import { useState, useEffect } from 'react';
import { Shield, Users, Save, Loader2, Info } from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';

interface SystemConfig {
  id: string;
  chave: string;
  valor: string;
  grupo: string;
  tipo: string;
}

export function LoginAcessosPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permitirAutocadastro, setPermitirAutocadastro] = useState(false);
  const [permitirEmailsLocal, setPermitirEmailsLocal] = useState(false);
  const [exigirAprovacao, setExigirAprovacao] = useState(true);
  const [permitirCadastroQrcode, setPermitirCadastroQrcode] = useState(true);

  useEffect(() => {
    async function fetchConfigs() {
      try {
        const { data } = await api.get<SystemConfig[]>('/admin/configuracoes');
        
        const selfRegConfig = data.find(c => c.chave === 'permitir_autocadastro_visitante');
        setPermitirAutocadastro(selfRegConfig ? selfRegConfig.valor === 'true' : false);

        const emailLocalConfig = data.find(c => c.chave === 'permitir_emails_ambiente_local_teste');
        setPermitirEmailsLocal(emailLocalConfig ? emailLocalConfig.valor === 'true' : false);

        const exigirAprovConfig = data.find(c => c.chave === 'exigir_aprovacao_dispositivo');
        setExigirAprovacao(exigirAprovConfig ? exigirAprovConfig.valor === 'true' : true);

        const qrcodeConfig = data.find(c => c.chave === 'permitir_cadastro_qrcode');
        setPermitirCadastroQrcode(qrcodeConfig ? qrcodeConfig.valor === 'true' : true);
      } catch (err) {
        console.error('Erro ao buscar configurações:', err);
        toast.error('Erro ao carregar as configurações do sistema.');
      } finally {
        setLoading(false);
      }
    }
    fetchConfigs();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/configuracoes/permitir_autocadastro_visitante', {
        valor: permitirAutocadastro ? 'true' : 'false'
      });
      await api.put('/admin/configuracoes/permitir_emails_ambiente_local_teste', {
        valor: permitirEmailsLocal ? 'true' : 'false'
      });
      await api.put('/admin/configuracoes/exigir_aprovacao_dispositivo', {
        valor: exigirAprovacao ? 'true' : 'false'
      });
      await api.put('/admin/configuracoes/permitir_cadastro_qrcode', {
        valor: permitirCadastroQrcode ? 'true' : 'false'
      });
      toast.success('Configurações salvas com sucesso!');
    } catch (err) {
      console.error('Erro ao salvar configuração:', err);
      toast.error('Erro ao salvar as configurações.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page animate-fadeIn" style={{ padding: '24px' }}>
      
      {/* ═══ HERO BANNER ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #1f2937 0%, #111827 40%, #0f172a 100%)',
        borderRadius: 20, 
        padding: '28px 32px', 
        marginBottom: 24, 
        position: 'relative', 
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
      }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(99,102,241,.1)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Shield size={18} style={{ color: '#8b5cf6' }} />
            <span style={{ color: 'rgba(255,255,255,.4)', fontSize: '.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.12em' }}>
              Sistema & Acessos
            </span>
          </div>
          <h1 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>Login e Acessos</h1>
          <p style={{ color: 'rgba(255,255,255,.45)', fontSize: '.82rem', marginTop: 4 }}>
            Gerencie as políticas de login, ativação de novas contas e permissões do sistema.
          </p>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Loader2 size={32} style={{ animation: 'lk-spin 1s linear infinite', color: '#8b5cf6' }} />
        </div>
      ) : (
        <div style={{
          background: 'var(--bg-card, white)', 
          borderRadius: 16,
          border: '1px solid var(--border-color, #e5e7eb)',
          padding: '24px',
          maxWidth: '800px',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)'
        }}>
          
          {/* Seção 1: Auto-Cadastro */}
          <h2 style={{ 
            fontSize: '1.1rem', 
            fontWeight: 700, 
            color: 'var(--text-main, #111827)', 
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Users size={20} style={{ color: '#8b5cf6' }} />
            Políticas de Auto-Cadastro
          </h2>

          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '16px',
            background: 'var(--bg-surface, #f9fafb)',
            borderRadius: '12px',
            border: '1px solid var(--border-color, #f3f4f6)',
            marginBottom: '20px'
          }}>
            <div>
              <h3 style={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--text-main, #1f2937)', margin: 0 }}>
                Permitir Auto-Cadastro de Visitantes
              </h3>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted, #6b7280)', margin: '4px 0 0 0', maxWidth: '500px' }}>
                Quando ativado, e-mails não previamente cadastrados no sistema poderão criar uma conta temporária como <strong>VISITANTE</strong> por meio de código OTP de verificação.
              </p>
            </div>
            
            <label style={{
              position: 'relative',
              display: 'inline-block',
              width: '52px',
              height: '28px',
              cursor: 'pointer',
              flexShrink: 0
            }}>
              <input 
                type="checkbox"
                checked={permitirAutocadastro}
                onChange={(e) => setPermitirAutocadastro(e.target.checked)}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                background: permitirAutocadastro ? '#8b5cf6' : '#d1d5db',
                transition: '0.3s',
                borderRadius: '34px',
                boxShadow: permitirAutocadastro ? '0 0 8px rgba(139,92,246,0.5)' : 'none'
              }}>
                <span style={{
                  position: 'absolute',
                  content: '""',
                  height: '22px',
                  width: '22px',
                  left: '3px',
                  bottom: '3px',
                  background: 'white',
                  transition: '0.3s',
                  borderRadius: '50%',
                  transform: permitirAutocadastro ? 'translateX(24px)' : 'translateX(0)'
                }} />
              </span>
            </label>
          </div>

          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '16px',
            background: 'var(--bg-surface, #f9fafb)',
            borderRadius: '12px',
            border: '1px solid var(--border-color, #f3f4f6)',
            marginBottom: '20px'
          }}>
            <div>
              <h3 style={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--text-main, #1f2937)', margin: 0 }}>
                Permitir Envio de E-mails em Ambiente Local/Teste
              </h3>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted, #6b7280)', margin: '4px 0 0 0', maxWidth: '500px' }}>
                Quando desativado, o envio de e-mails em ambientes locais (localhost/127.0.0.1) ou testes é ignorado por segurança, exibindo os links e códigos apenas nos logs.
              </p>
            </div>
            
            <label style={{
              position: 'relative',
              display: 'inline-block',
              width: '52px',
              height: '28px',
              cursor: 'pointer',
              flexShrink: 0
            }}>
              <input 
                type="checkbox"
                checked={permitirEmailsLocal}
                onChange={(e) => setPermitirEmailsLocal(e.target.checked)}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                background: permitirEmailsLocal ? '#8b5cf6' : '#d1d5db',
                transition: '0.3s',
                borderRadius: '34px',
                boxShadow: permitirEmailsLocal ? '0 0 8px rgba(139,92,246,0.5)' : 'none'
              }}>
                <span style={{
                  position: 'absolute',
                  content: '""',
                  height: '22px',
                  width: '22px',
                  left: '3px',
                  bottom: '3px',
                  background: 'white',
                  transition: '0.3s',
                  borderRadius: '50%',
                  transform: permitirEmailsLocal ? 'translateX(24px)' : 'translateX(0)'
                }} />
              </span>
            </label>
          </div>

          <div style={{
            display: 'flex',
            gap: '10px',
            padding: '12px 16px',
            background: 'rgba(139,92,246,0.06)',
            border: '1px solid rgba(139,92,246,0.15)',
            borderRadius: '12px',
            marginBottom: '24px'
          }}>
            <Info size={18} style={{ color: '#8b5cf6', flexShrink: 0, marginTop: '2px' }} />
            <div style={{ fontSize: '0.78rem', color: '#6d28d9', lineHeight: 1.4 }}>
              <strong>Nota Importante:</strong> Usuários auto-cadastrados serão adicionados com perfil limitado <strong>VISITANTE</strong> por motivos de segurança. Um administrador precisará alterar seu perfil posteriormente caso necessitem de acessos mais amplos.
            </div>
          </div>

          {/* Seção 2: Segurança e Dispositivos */}
          <div style={{ borderTop: '1px solid var(--border-color, #e5e7eb)', margin: '24px 0', paddingTop: '20px' }}>
            <h2 style={{ 
              fontSize: '1.1rem', 
              fontWeight: 700, 
              color: 'var(--text-main, #111827)', 
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Shield size={20} style={{ color: '#8b5cf6' }} />
              Políticas de Segurança e Dispositivos
            </h2>

            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '16px',
              background: 'var(--bg-surface, #f9fafb)',
              borderRadius: '12px',
              border: '1px solid var(--border-color, #f3f4f6)',
              marginBottom: '20px'
            }}>
              <div>
                <h3 style={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--text-main, #1f2937)', margin: 0 }}>
                  Exigir Confirmação Biométrica / Aprovação de Dispositivo
                </h3>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted, #6b7280)', margin: '4px 0 0 0', maxWidth: '500px' }}>
                  Quando ativado, logins em novos navegadores ou dispositivos não autorizados exigiriam confirmação por biometria ou push no celular previamente vinculado do usuário.
                </p>
              </div>
              
              <label style={{
                position: 'relative',
                display: 'inline-block',
                width: '52px',
                height: '28px',
                cursor: 'pointer',
                flexShrink: 0
              }}>
                <input 
                  type="checkbox"
                  checked={exigirAprovacao}
                  onChange={(e) => setExigirAprovacao(e.target.checked)}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  background: exigirAprovacao ? '#8b5cf6' : '#d1d5db',
                  transition: '0.3s',
                  borderRadius: '34px',
                  boxShadow: exigirAprovacao ? '0 0 8px rgba(139,92,246,0.5)' : 'none'
                }}>
                  <span style={{
                    position: 'absolute',
                    content: '""',
                    height: '22px',
                    width: '22px',
                    left: '3px',
                    bottom: '3px',
                    background: 'white',
                    transition: '0.3s',
                    borderRadius: '50%',
                    transform: exigirAprovacao ? 'translateX(24px)' : 'translateX(0)'
                  }} />
                </span>
              </label>
            </div>

            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '16px',
              background: 'var(--bg-surface, #f9fafb)',
              borderRadius: '12px',
              border: '1px solid var(--border-color, #f3f4f6)',
              marginBottom: '20px'
            }}>
              <div>
                <h3 style={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--text-main, #1f2937)', margin: 0 }}>
                  Permitir Pareamento de Dispositivo via QR Code
                </h3>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted, #6b7280)', margin: '4px 0 0 0', maxWidth: '500px' }}>
                  Quando desativado, o wizard de pareamento e a exibição do QR Code de vínculo de dispositivo celular serão desabilitados no sistema por motivos de simplificação operacional.
                </p>
              </div>
              
              <label style={{
                position: 'relative',
                display: 'inline-block',
                width: '52px',
                height: '28px',
                cursor: 'pointer',
                flexShrink: 0
              }}>
                <input 
                  type="checkbox"
                  checked={permitirCadastroQrcode}
                  onChange={(e) => setPermitirCadastroQrcode(e.target.checked)}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  background: permitirCadastroQrcode ? '#8b5cf6' : '#d1d5db',
                  transition: '0.3s',
                  borderRadius: '34px',
                  boxShadow: permitirCadastroQrcode ? '0 0 8px rgba(139,92,246,0.5)' : 'none'
                }}>
                  <span style={{
                    position: 'absolute',
                    content: '""',
                    height: '22px',
                    width: '22px',
                    left: '3px',
                    bottom: '3px',
                    background: 'white',
                    transition: '0.3s',
                    borderRadius: '50%',
                    transform: permitirCadastroQrcode ? 'translateX(24px)' : 'translateX(0)'
                  }} />
                </span>
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid var(--border-color, #e5e7eb)', paddingTop: '16px' }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: '#8b5cf6',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.88rem',
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'opacity 0.2s',
                opacity: saving ? 0.7 : 1,
                boxShadow: '0 4px 12px rgba(139,92,246,0.3)'
              }}
              onMouseEnter={e => { if (!saving) e.currentTarget.style.opacity = '0.9'; }}
              onMouseLeave={e => { if (!saving) e.currentTarget.style.opacity = '1'; }}
            >
              {saving ? (
                <>
                  <Loader2 size={16} style={{ animation: 'lk-spin 1s linear infinite' }} />
                  Salvando...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Salvar Configurações
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
